import { cn } from '@shadcn/lib/utils';
import { LockIcon } from 'lucide-react';
import { FunctionComponent, SVGProps } from 'react';

import { Card } from '../../../shadcn/components/ui/card';
import { Header } from '../../atoms/header/header';
import { IllustrationName, illustrations } from '../../atoms/illustration/illustration';
import { Text } from '../../atoms/text/text';

type ContentCardState = 'active' | 'highlighted' | 'locked' | 'disabled';

type ContentCardProps = {
  readonly image: IllustrationName | FunctionComponent<SVGProps<SVGSVGElement>>;
  readonly title: string;
  readonly description?: string;
  readonly state: ContentCardState;
  readonly onClick?: () => void;
};

const ContentCard = ({ image, state, title, description, onClick }: ContentCardProps) => {
  const Image = typeof image === 'string' ? illustrations[image] : image;

  return (
    <Card className="p-0 overflow-hidden w-full h-auto">
      <button
        className={cn('relative w-full min-h-[96px] min-w-[300px] flex', {
          'bg-paper-mid': state === 'locked',
          'bg-neutral-100': state === 'disabled',
          'cursor-pointer': state !== 'locked',
        })}
        disabled={state === 'locked' || state === 'disabled'}
        onClick={onClick}
      >
        <div className="absolute top-0 left-0 right-0 bottom-0 object-cover inline-flex justify-end w-full">
          <Image />
        </div>
        <div className="p-4 flex flex-col justify-end items-start max-w-3/4 z-1">
          {state === 'locked' && <LockIcon className="text-teal-light mb-1" size={16} />}
          <Header
            className={cn(
              'text-left break-words hyphens-auto',
              state === 'highlighted' ? '!text-teal-mid' : '!text-teal-light',
              {
                '!text-neutral-600': state === 'disabled',
              }
            )}
            level="h3"
          >
            {title}
          </Header>
          {description && (
            <Text
              className={cn('!text-teal-light text-left', {
                '!text-neutral-600': state === 'disabled',
              })}
            >
              {description}
            </Text>
          )}
        </div>
      </button>
    </Card>
  );
};

export { ContentCard };
export type { ContentCardState };
